'use client'
import { createContext, Dispatch, ReactNode, SetStateAction, useContext, useEffect, useState } from "react";
import { Pitch, Pitcher } from "./types";
import * as THREE from 'three'
import { deserialize, serialize } from "./serialize";

/**
 * Local Storage Types
 * Add a type for every (key, value) pair
 */
type KeyValuePairs ={
    // General UI
    NAV_MENU_STATE: Record<string, boolean>
    // PWA
    INSTALL_PROMPT_DECLINED: boolean
    // Pitch-Designer
    PITCHERS: Pitcher[]
    PITCHES: Pitch[]
}

const defaultValues: KeyValuePairs = {
    // General UI
    NAV_MENU_STATE: {},
    // PWA
    INSTALL_PROMPT_DECLINED: false,
    // Pitch-Designer
    PITCHERS: [],
    PITCHES: []
}


// Public Functions
/**
 * Saves an item to localStorage
 * @param key - localStorage key
 * @param value | undefined
 */
export async function setLocalStoreItem<K extends keyof KeyValuePairs>(key: K, value: KeyValuePairs[K]) {
    localStorage.setItem(key, JSON.stringify(serialize(value)))
}
/**
 * Fetches an item from localStorage
 * @param key 
 * @returns value | null
 */
export function getLocalStoreItem<K extends keyof KeyValuePairs>(key: K): KeyValuePairs[K] | null {
    try {
        const item = JSON.parse(localStorage.getItem(key) || "null")
        if (!item) return null
        return deserialize(item) as KeyValuePairs[K]
    } catch {
        return null
    }
}

/**
 * Removes an item from localStorage
 * @param key 
 */
export function removeLocalStoreItem<K extends keyof KeyValuePairs>(key: K) {
    localStorage.removeItem(key)
}
/**
 * Clears all items in localStorage
 */
export function clearLocalStore() {
    localStorage.clear()
}
export function useLocalStoreItem<K extends keyof KeyValuePairs>(key: K): [KeyValuePairs[K] | undefined, Dispatch<SetStateAction<KeyValuePairs[K] | undefined>>] {
    const [item, setItem] = useState<KeyValuePairs[K] | undefined>(() => {
        if (typeof window === 'undefined') return undefined

        return getLocalStoreItem(key) ?? undefined
    })

    // Listen for storage events to keep app open in different tabs in sync
    useEffect(() => {
        console.log('useLocalStore initialized')
        const handleStorageChange = (event: StorageEvent) => {
            if (event.key === key && event.newValue) {                
                setItem((JSON.parse(deserialize(event.newValue)) as KeyValuePairs[K]) ?? undefined )
            }
        }

        window.addEventListener("storage", handleStorageChange)
        return () => window.removeEventListener('storage', handleStorageChange)
    }, [key])

    useEffect(() => {
        if (item) {
            setLocalStoreItem(key, item)
        }
    }, [item])

    return [item, setItem]
}

type LocalStoreContextType = {
    values: KeyValuePairs
    setValues: Dispatch<SetStateAction<KeyValuePairs>> | undefined
}
const LocalStoreContext = createContext<LocalStoreContextType>({values: defaultValues, setValues: undefined})
export const LocalStoreProvider = ({children}: {children: ReactNode}) => {
    const [prevValues, setPrevValues] = useState<KeyValuePairs | undefined>()
    const [values, setValues] = useState<KeyValuePairs>(() => {
        if (typeof window === 'undefined') return defaultValues
        return defaultValues
    })

    // Set values from localStorage on load
    useEffect(() => {
        setValues({
            // General UI
            NAV_MENU_STATE: getLocalStoreItem('NAV_MENU_STATE') ?? defaultValues.NAV_MENU_STATE,
            // PWA
            INSTALL_PROMPT_DECLINED: getLocalStoreItem('INSTALL_PROMPT_DECLINED') ?? defaultValues.INSTALL_PROMPT_DECLINED,
            // Pitch-Designer
            PITCHERS: getLocalStoreItem('PITCHERS') ?? defaultValues.PITCHERS,
            PITCHES: getLocalStoreItem('PITCHES') ?? defaultValues.PITCHES,
        })
    },[])

    // Find changes and update localStorage
    useEffect(() => {
        if (prevValues !== undefined) {
            (Object.keys(values) as Array<keyof KeyValuePairs>).forEach((key) => {
                if (prevValues[key] !== values[key]) {
                    console.log('updating ', key, ' : ', values[key], ' in localStorage')
                    setLocalStoreItem(key, values[key])
                }
            })
        }

        setPrevValues(values)
    }, [values])


    
    return (
        <LocalStoreContext.Provider value={{values, setValues}}>
            {children}
        </LocalStoreContext.Provider>
    )
}
export const useLocalStore = () => useContext(LocalStoreContext)

